import { trackException } from 'helpers/errorTracker';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch, useHistory } from 'react-router-dom';

import { useCallback, useMemo, useState } from 'react';
import { roles } from 'helpers/roles';
import { useUpdateNetworkKindMutation } from 'services/organizations';
import { useAddNetworkAdminMutation } from 'services/networkAdmins';
import toast from 'react-hot-toast';
import strings from 'strings';
import { updateNetwork } from 'actions/networksActions';
import { trackEvent } from 'helpers/analytics';
import { setUser } from '../../../actions/userActions';
import { networksSelector, userSelector } from '../../../redux/selectors';
import { useUpdateUserMutation } from '../../../services/user';
import { useUpdateUserGoalsMutation } from '../../../services/userV2';
import { legalDocumentsVersion } from '../../../settings/legalDocumentsVersion';

export const useOnboarding = () => {
  const { path, params } = useRouteMatch();
  const { networks } = useSelector(networksSelector);
  const user = useSelector(userSelector);
  const network = useMemo(
    () => Object.values(networks).find(({ slug }) => slug === params.networkSlug),
    [networks, params.networkSlug],
  );
  const [updateUser, { isLoading: isLoadingUpdateUser }] = useUpdateUserMutation();
  const [add, { isLoading: isLoadingAddAdmin }] = useAddNetworkAdminMutation();

  const dispatch = useDispatch();
  const history = useHistory();
  const [users, setUsers] = useState([
    {
      id: 1,
      name: '',
      email: '',
      role: roles[1],
    },
    {
      id: 2,
      name: '',
      email: '',
      role: roles[1],
    },
    {
      id: 3,
      name: '',
      email: '',
      role: roles[1],
    },
  ]);
  const [networkKind, setNetworkKind] = useState(network?.kind);
  const [goals, setGoals] = useState(new Set(network?.userGoals ?? []));
  const slug = network?.slug;
  const [updateUserGoals, { isLoading: isLoadingUpdateUserGoals }] = useUpdateUserGoalsMutation();
  const [updateKind, { isLoading: isLoadingUpdateNetworkKind }] = useUpdateNetworkKindMutation();

  const addAdmin = useCallback(
    async (usersToAdd) => Promise.allSettled(usersToAdd.map((admin) => add({ networkId: network?.id, admin }))),
    [add, network],
  );

  const getNextStep = useCallback(
    async (step, props) => {
      switch (step) {
        case 'profile':
          trackEvent('admin_onboarding:finish_step', { step_name: 'account' });
          return `/networks/${slug}/welcome/onboarding/network-kind`;
        case 'network-kind':
          setNetworkKind(props);
          trackEvent('admin_onboarding:finish_step', { step_name: 'network_type' });
          return `/networks/${slug}/welcome/onboarding/goals`;
        case 'goals':
          setGoals(props);
          trackEvent('admin_onboarding:finish_step', { step_name: 'goals' });
          return `/networks/${slug}/welcome/onboarding/linkedin-sync`;
        case 'linkedin-sync':
          trackEvent('admin_onboarding:finish_step', { step_name: 'linkedin_sync' });
          return `/networks/${slug}/welcome/onboarding/team`;
        case 'team':
          trackEvent('admin_onboarding:finish_step', { step_name: 'invite_team' });
          trackEvent('admin_onboarding:complete');

          return `/networks/${slug}/home`;
        default:
          trackEvent('admin_onboarding:complete');

          return `/networks/${slug}/home`;
      }
    },
    [slug],
  );

  const handleNextStep = useCallback(
    async (props) => {
      const nextStep = await getNextStep(params.step, props);
      history.push(nextStep);
    },
    [getNextStep, history, params],
  );

  const updateUserProfile = async ({ avatarUrl, firstName, lastName, password }) => {
    const options = {
      avatarUrl,
      firstName,
      lastName,
      password,
      passwordConfirmation: password,
      termsPolicyVersion: legalDocumentsVersion.termsPolicyVersion,
      privacyPolicyVersion: legalDocumentsVersion.privacyPolicyVersion,
    };

    return updateUser(options)
      .then((response) => {
        handleNextStep();
        return response;
      })
      .catch((error) => {
        trackException(error);

        throw error;
      });
  };

  const updateNetworkKind = async (kind) => {
    setNetworkKind(kind);
    const response = await updateKind({ networkId: network?.id, kind });

    if (response.error) {
      toast(strings.genericError, {
        icon: 'error',
        id: 'update-network-kind',
      });
      return;
    }

    dispatch(
      updateNetwork({
        id: network?.id,
        kind: networkKind,
        kindSetupByCurrentUser: true,
      }),
    );

    handleNextStep(kind);
  };

  const updateGoals = async (goalsToUpdate) => {
    setGoals(goalsToUpdate);

    const response = await updateUserGoals({ networkId: network?.id, goals: Array.from(goalsToUpdate) });

    if (response.error) {
      toast(strings.genericError, {
        icon: 'error',
        id: 'update-user-goals',
      });
      return;
    }

    handleNextStep(goalsToUpdate);
  };

  const updateTeam = async (props) => {
    setUsers((prev) => {
      const map = new Map();
      props.usersToAdd.forEach((item) => {
        map.set(item.id, item);
      });
      return prev.map((item) => {
        if (map.has(item.id)) {
          return map.get(item.id);
        }
        return item;
      });
    });

    if (!props.skip && props.usersToAdd.length > 0) {
      const results = await addAdmin(props.usersToAdd);
      const hasFailure = results.some((r) => r.value.error);
      if (hasFailure) {
        toast(strings.genericError, {
          icon: 'error',
          id: 'update-team',
        });

        return;
      }
    }
    const { avatarUrl, firstName, lastName } = user;

    if (!user.onboardingCompleted) {
      updateUser({
        onboardingCompleted: true,
      });
    }

    dispatch(
      setUser({
        avatarUrl,
        firstName,
        lastName,
        kind: networkKind,
        goals: Array.from(goals),
        onboardingCompleted: true,
      }),
    );

    dispatch(
      updateNetwork({
        id: network?.id,
        kind: networkKind,
        userGoals: Array.from(goals),
      }),
    );

    handleNextStep(props);
  };

  return {
    activeNetwork: network,
    networks,
    path,
    isLoadingUpdateUser,
    updateUserProfile,
    handleNextStep,
    users,
    networkKind,
    goals,
    setUsers,
    setNetworkKind,
    setGoals,
    updateUserGoals,
    isLoadingUpdateUserGoals,
    updateNetworkKind,
    isLoadingUpdateNetworkKind,
    isLoadingAddAdmin: isLoadingAddAdmin || isLoadingUpdateUser,
    user,
    params,
    updateTeam,
    updateGoals,
    showNetworkKindStep: network?.kindSetupByCurrentUser || network?.kindSetupAt === null,
    skipSignUp: user?.onboardingCompleted && (user.hasPassword || user.oauthProviders.length > 0),
    profileMode: user?.hasPassword || user?.oauthProviders.length > 0 ? 'password' : 'email',
  };
};
